import React, { useState } from "react";
import { EMITab } from "./calc/EMITab";
import { SIPTab } from "./calc/SIPTab";
import { LumpsumTab } from "./calc/LumpsumTab";
import { FDTab } from "./calc/FDTab";
import { RDTab } from "./calc/RDTab";
import { BondTab } from "./calc/BondTab";
import { PPFTab } from "./calc/PPFTab";
import { EPFTab } from "./calc/EPFTab";
import { NPSTab } from "./calc/NPSTab";

const TABS = {
  emi: {
    label: "EMI",
    title: "Loan EMI Calculator",
    subtitle: "Calculate your monthly EMI instantly. No signup required.",
    Component: EMITab,
  },
  sip: {
    label: "SIP",
    title: "SIP Calculator",
    subtitle: "Project the maturity value of your monthly SIP investment.",
    Component: SIPTab,
  },
  lumpsum: {
    label: "Lumpsum",
    title: "Lumpsum Calculator",
    subtitle: "See how a one-time mutual fund investment grows over time.",
    Component: LumpsumTab,
  },
  fd: {
    label: "FD",
    title: "Fixed Deposit Calculator",
    subtitle: "Estimate your FD maturity with quarterly compounding.",
    Component: FDTab,
  },
  rd: {
    label: "RD",
    title: "Recurring Deposit Calculator",
    subtitle: "Plan a monthly RD and see how interest compounds over time.",
    Component: RDTab,
  },
  bond: {
    label: "Bonds",
    title: "Bond Returns Calculator",
    subtitle: "Project your bond returns with coupon reinvestment.",
    Component: BondTab,
  },
  ppf: {
    label: "PPF",
    title: "PPF Calculator",
    subtitle: "Project your tax-free Public Provident Fund corpus.",
    Component: PPFTab,
  },
  epf: {
    label: "EPF",
    title: "EPF Calculator",
    subtitle: "Estimate your retirement corpus from monthly EPF contributions.",
    Component: EPFTab,
  },
  nps: {
    label: "NPS",
    title: "NPS Calculator",
    subtitle: "Plan your retirement with the National Pension System.",
    Component: NPSTab,
  },
};

const CATEGORIES = [
  { key: "loan", label: "Loans", tagline: "For borrowers", tabs: ["emi"] },
  { key: "mf", label: "Mutual Funds", tagline: "SIP & Lumpsum returns", tabs: ["sip", "lumpsum"] },
  { key: "deposits", label: "Deposits & Bonds", tagline: "Safe fixed-income returns", tabs: ["fd", "rd", "bond"] },
  { key: "schemes", label: "Govt Schemes", tagline: "PPF, EPF & NPS", tabs: ["ppf", "epf", "nps"] },
];

export const Calculator = () => {
  const [category, setCategory] = useState("mf");
  const [activeByCat, setActiveByCat] = useState({
    loan: "emi",
    mf: "sip",
    deposits: "fd",
    schemes: "ppf",
  });

  const currentCat = CATEGORIES.find((c) => c.key === category) || CATEGORIES[0];
  const activeKey = activeByCat[category] || currentCat.tabs[0];
  const current = TABS[activeKey];
  const ActiveTab = current.Component;

  const setActive = (key) => setActiveByCat((s) => ({ ...s, [category]: key }));

  return (
    <section
      id="calculator"
      className="py-20"
      style={{ backgroundColor: "#F7F9FC" }}
      data-testid="calculator-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <span
            className="inline-block rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider"
            style={{ backgroundColor: "#E1F5EE", color: "#0F6E56" }}
            data-testid="calculator-eyebrow"
          >
            Free Tools
          </span>
          <h2
            className="font-display font-bold mt-4"
            style={{ color: "#1A202C", fontSize: 32, letterSpacing: "-0.01em" }}
            data-testid="calculator-heading"
          >
            {current.title}
          </h2>
          <p className="mt-3" style={{ color: "#718096", fontSize: 16 }}>
            {current.subtitle}
          </p>
        </div>

        {/* Category switcher — primary level (responsive grid) */}
        <div className="mb-5" data-testid="calculator-categories">
          <div
            className="grid grid-cols-2 lg:grid-cols-4 gap-2 max-w-3xl mx-auto"
            role="tablist"
          >
            {CATEGORIES.map((c) => {
              const isActive = c.key === category;
              return (
                <button
                  key={c.key}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setCategory(c.key)}
                  className="rounded-xl transition-all duration-150 text-left min-w-0"
                  style={{
                    padding: "12px 14px",
                    backgroundColor: isActive ? "#0D1117" : "#FFFFFF",
                    color: isActive ? "#FFFFFF" : "#1A202C",
                    border: isActive ? "1px solid #0D1117" : "1px solid #E2E8F0",
                  }}
                  data-testid={`calc-cat-${c.key}`}
                >
                  <div className="font-display font-semibold truncate" style={{ fontSize: 14 }}>
                    {c.label}
                  </div>
                  <div
                    className="truncate"
                    style={{
                      fontSize: 11,
                      color: isActive ? "rgba(255,255,255,0.55)" : "#718096",
                      marginTop: 2,
                    }}
                  >
                    {c.tagline}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Sub-tabs for current category */}
        <div className="flex justify-center mb-8 px-2" data-testid="calculator-tabs">
          <div
            className="inline-flex rounded-full p-1 overflow-x-auto max-w-full no-scrollbar"
            style={{ backgroundColor: "#FFFFFF", border: "1px solid #E2E8F0" }}
            role="tablist"
          >
            {currentCat.tabs.map((key) => {
              const t = TABS[key];
              const isActive = key === activeKey;
              return (
                <button
                  key={key}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(key)}
                  className="rounded-full transition-all duration-150 whitespace-nowrap"
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    padding: "8px 18px",
                    backgroundColor: isActive ? "#1D9E75" : "transparent",
                    color: isActive ? "#FFFFFF" : "#4A5568",
                  }}
                  data-testid={`calc-tab-${key}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Card */}
        <div
          className="mx-auto rounded-2xl bg-white overflow-hidden"
          style={{
            maxWidth: 960,
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          }}
          data-testid="calculator-card"
        >
          <ActiveTab />
        </div>
      </div>
    </section>
  );
};
